import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS, SPEC_DATA_TYPES } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tree,
  TreeSelect,
  Typography,
} from 'antd';
import { useState } from 'react';
import { api, errorText } from '../../api/client';
import type { Brand, Category, Location, SpecDefinition } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { categoryTree, locationTree } from './categoryTree';

const TYPE_LABEL = { number: 'Số', text: 'Chữ', boolean: 'Có/không', option: 'Lựa chọn' } as const;

/** Danh mục, định nghĩa thông số, thương hiệu, khu vực (PRD 6.1 mục 2, 4). */
export function CatalogSetupPage() {
  const { can } = useAuth();
  const canEdit = can(PERMISSIONS.CATALOG_MANAGE);
  const { message } = App.useApp();
  const qc = useQueryClient();
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/admin/categories'),
  });
  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<Brand[]>('/admin/brands'),
  });
  const locs = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/admin/locations'),
  });
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const specs = useQuery({
    queryKey: ['spec-defs', selectedCat],
    queryFn: () => api.get<SpecDefinition[]>(`/admin/categories/${selectedCat}/spec-definitions`),
    enabled: !!selectedCat,
  });
  const [modal, setModal] = useState<null | 'category' | 'brand' | 'spec'>(null);
  const [form] = Form.useForm();

  const submit = async () => {
    const v = await form.validateFields();
    try {
      if (modal === 'category') await api.post('/admin/categories', v);
      if (modal === 'brand') await api.post('/admin/brands', v);
      if (modal === 'spec') {
        const options =
          v.data_type === 'option'
            ? String(v.options_text ?? '')
                .split('\n')
                .map((l: string) => l.trim())
                .filter(Boolean)
                .map((l: string) => {
                  const [value, label] = l.split('|').map((s) => s.trim());
                  return { value: value!, label: label || value! };
                })
            : undefined;
        await api.post('/admin/spec-definitions', {
          ...v,
          options_text: undefined,
          options,
          category_id: selectedCat,
        });
      }
      message.success('Đã lưu');
      setModal(null);
      void qc.invalidateQueries();
    } catch (e) {
      message.error(errorText(e));
    }
  };

  const catName = cats.data?.find((c) => c.id === selectedCat)?.name;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={10}>
        <Card
          title="Danh mục"
          extra={
            canEdit && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => (form.resetFields(), setModal('category'))}
              >
                Thêm
              </Button>
            )
          }
        >
          <Typography.Paragraph type="secondary">
            Bấm vào danh mục để xem thông số kỹ thuật áp dụng.
          </Typography.Paragraph>
          <Tree
            treeData={categoryTree(cats.data ?? [])}
            defaultExpandAll
            onSelect={(k) => setSelectedCat((k[0] as string) ?? null)}
            selectedKeys={selectedCat ? [selectedCat] : []}
          />
        </Card>
        <Card
          title="Thương hiệu"
          style={{ marginTop: 16 }}
          extra={
            canEdit && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => (form.resetFields(), setModal('brand'))}
              >
                Thêm
              </Button>
            )
          }
        >
          <Space wrap>
            {(brands.data ?? []).map((b) => (
              <Tag key={b.id} color={b.is_active ? 'blue' : 'default'}>
                {b.name}
              </Tag>
            ))}
          </Space>
        </Card>
        <Card title="Khu vực phục vụ" style={{ marginTop: 16 }}>
          <Typography.Paragraph type="secondary">
            Theo D-10: chỉ cấp tỉnh; vùng tỉnh cũ đã sáp nhập là con của tỉnh mới.
          </Typography.Paragraph>
          <Tree treeData={locationTree(locs.data ?? [])} defaultExpandAll selectable={false} />
        </Card>
      </Col>
      <Col xs={24} lg={14}>
        <Card
          title={selectedCat ? `Thông số áp dụng cho: ${catName}` : 'Thông số kỹ thuật'}
          extra={
            canEdit &&
            selectedCat && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => (form.resetFields(), setModal('spec'))}
              >
                Thêm thông số
              </Button>
            )
          }
        >
          {!selectedCat ? (
            <Typography.Text type="secondary">Chọn một danh mục bên trái.</Typography.Text>
          ) : (
            <Table<SpecDefinition>
              size="small"
              rowKey="id"
              pagination={false}
              loading={specs.isLoading}
              dataSource={specs.data}
              columns={[
                { title: 'Tên', dataIndex: 'label' },
                {
                  title: 'Khóa',
                  dataIndex: 'spec_key',
                  render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
                },
                { title: 'Kiểu', render: (_, r) => TYPE_LABEL[r.data_type] },
                { title: 'Đơn vị', dataIndex: 'unit' },
                {
                  title: '',
                  render: (_, r) => (
                    <Space size={4}>
                      {r.is_required && <Tag color="red">Bắt buộc</Tag>}
                      {r.is_filterable && <Tag color="blue">Lọc</Tag>}
                      {r.category_id !== selectedCat && <Tag>Kế thừa</Tag>}
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Card>
      </Col>

      <Modal
        open={modal !== null}
        onCancel={() => setModal(null)}
        onOk={() => void submit()}
        okText="Lưu"
        forceRender
        title={
          modal === 'category'
            ? 'Thêm danh mục'
            : modal === 'brand'
              ? 'Thêm thương hiệu'
              : 'Thêm thông số'
        }
      >
        <Form form={form} layout="vertical">
          {modal === 'category' && (
            <>
              <Form.Item name="name" label="Tên danh mục" rules={[{ required: true, min: 2 }]}>
                <Input />
              </Form.Item>
              <Form.Item name="parent_id" label="Danh mục cha">
                <TreeSelect
                  allowClear
                  treeData={categoryTree(cats.data ?? [])}
                  treeDefaultExpandAll
                />
              </Form.Item>
              <Form.Item name="sort_order" label="Thứ tự">
                <InputNumber min={0} />
              </Form.Item>
            </>
          )}
          {modal === 'brand' && (
            <Form.Item name="name" label="Tên thương hiệu" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          {modal === 'spec' && (
            <>
              <Form.Item name="label" label="Tên hiển thị" rules={[{ required: true }]}>
                <Input placeholder="Chiều cao làm việc" />
              </Form.Item>
              <Form.Item
                name="spec_key"
                label="Khóa (snake_case, không đổi được)"
                rules={[
                  {
                    required: true,
                    pattern: /^[a-z][a-z0-9_]{1,99}$/,
                    message: 'vd: working_height',
                  },
                ]}
              >
                <Input placeholder="working_height" />
              </Form.Item>
              <Space wrap>
                <Form.Item name="data_type" label="Kiểu" rules={[{ required: true }]}>
                  <Select
                    style={{ width: 160 }}
                    options={SPEC_DATA_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
                  />
                </Form.Item>
                <Form.Item name="unit" label="Đơn vị">
                  <Input style={{ width: 100 }} placeholder="m, kg" />
                </Form.Item>
                <Form.Item name="is_required" label="Bắt buộc" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item name="is_filterable" label="Dùng để lọc" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Space>
              <Form.Item noStyle shouldUpdate={(a, b) => a.data_type !== b.data_type}>
                {({ getFieldValue }) =>
                  getFieldValue('data_type') === 'option' && (
                    <Form.Item
                      name="options_text"
                      label="Các lựa chọn (mỗi dòng: giá_trị|Nhãn)"
                      rules={[{ required: true }]}
                    >
                      <Input.TextArea rows={4} placeholder={'electric|Điện\ndiesel|Diesel'} />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Row>
  );
}
